import { toLocalLength, toLocalPoint } from '@src/utility/frameGeometry';

import type { Stroke } from '@src/types.ts/comment';
import type { FrameRect, LocalPoint } from '@src/utility/frameGeometry';

/** Arrow head length, as a fraction of the frame height. */
const ARROW_HEAD_FRACTION = 0.035;
/** Half-angle of the arrow head, in radians (~28°). */
const ARROW_HEAD_SPREAD = 0.5;

const move = (p: LocalPoint) => `M ${p.x} ${p.y}`;
const line = (p: LocalPoint) => `L ${p.x} ${p.y}`;

/**
 * Build the SVG path for one stroke, in pixels local to the frame box.
 *
 * Kept out of the component so the shape maths — which is the part that can be
 * subtly wrong — can be asserted directly, without rendering anything.
 *
 * Every tool renders as a single stroked path with no fill, so the caller sets
 * one set of stroke attributes and does not branch on the tool.
 */
export function strokePath(stroke: Stroke, rect: FrameRect): string {
  const points = stroke.points.map(point => toLocalPoint(point, rect));
  if (points.length === 0) return '';
  const start = points[0];
  const end = points[points.length - 1];

  switch (stroke.tool) {
    case 'freehand':
      return [move(start), ...points.slice(1).map(line)].join(' ');

    case 'line':
      return `${move(start)} ${line(end)}`;

    case 'rect':
      // Drawn from either corner, so normalise before emitting — dragging up
      // and left must produce the same box as dragging down and right.
      return rectPath(start, end);

    case 'arrow':
      return arrowPath(start, end, rect);
  }
}

function rectPath(a: LocalPoint, b: LocalPoint): string {
  const left = Math.min(a.x, b.x);
  const right = Math.max(a.x, b.x);
  const top = Math.min(a.y, b.y);
  const bottom = Math.max(a.y, b.y);
  return [
    move({ x: left, y: top }),
    line({ x: right, y: top }),
    line({ x: right, y: bottom }),
    line({ x: left, y: bottom }),
    'Z',
  ].join(' ');
}

/**
 * A shaft plus two barbs, as one open path.
 *
 * The head is sized from the frame rather than from the shaft, so a short arrow
 * still gets a head large enough to read — but it is capped at a third of the
 * shaft, so a very short arrow does not become mostly head.
 */
function arrowPath(tail: LocalPoint, tip: LocalPoint, rect: FrameRect): string {
  const dx = tip.x - tail.x;
  const dy = tip.y - tail.y;
  const shaft = Math.hypot(dx, dy);
  if (shaft === 0) return move(tail);

  const head = Math.min(toLocalLength(ARROW_HEAD_FRACTION, rect), shaft / 3);
  const angle = Math.atan2(dy, dx);

  const barb = (offset: number): LocalPoint => ({
    x: tip.x - head * Math.cos(angle + offset),
    y: tip.y - head * Math.sin(angle + offset),
  });

  return [
    move(tail),
    line(tip),
    move(barb(ARROW_HEAD_SPREAD)),
    line(tip),
    line(barb(-ARROW_HEAD_SPREAD)),
  ].join(' ');
}
